/**
 * Cart Storage Utility
 *
 * Ensures the 'cartData' key always exists in localStorage
 * to prevent any conflicts or errors during cart operations.
 */
import { encryptData, decryptData } from "./cryptcart";

/**
 * Ensures cartData key exists in localStorage.
 * If it doesn't exist or is invalid, initializes it with an empty array.
 * @returns {Array} The current cart data
 */
export const ensureCartData = () => {
    try {
        const cartData = localStorage.getItem('cartData');

        // If cartData doesn't exist, create it with empty array
        if (cartData === null || cartData === undefined) {
            localStorage.setItem('cartData', encryptData([]));
            return [];
        }

        // Try to decrypt existing data
        const parsed = decryptData(cartData);

        // If parsed data is not an array, reset it
        if (!Array.isArray(parsed)) {
            localStorage.setItem('cartData', encryptData([]));
            return [];
        }

        return parsed;
    } catch (error) {
        // If any error occurs (e.g., decryption failure), reset to empty array
        console.error('Error reading cartData from localStorage:', error);
        localStorage.setItem('cartData', encryptData([]));
        return [];
    }
};

/**
 * Gets cart data from localStorage, ensuring the key exists first.
 * @returns {Array} The current cart data
 */
export const getCartData = () => {
    return ensureCartData();
};

/**
 * Sets cart data in localStorage.
 * @param {Array} data - The cart data to save
 */
export const setCartData = (data) => {
    try {
        const dataToSave = Array.isArray(data) ? data : [];
        localStorage.setItem('cartData', encryptData(dataToSave));
    } catch (error) {
        console.error('Error saving cartData to localStorage:', error);
    }
};

/**
 * Clears cart data (sets to empty array, doesn't remove the key).
 */
export const clearCartData = () => {
    localStorage.setItem('cartData', encryptData([]));
};

/**
 * Initialize cart data on app load.
 * Call this early in your app initialization.
 */
export const initializeCartData = () => {
    ensureCartData();
};

export default {
    ensureCartData,
    getCartData,
    setCartData,
    clearCartData,
    initializeCartData
};
